import React, { FC, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store";
import { I18n, Translate } from "react-redux-i18n";
import { Button, DatePicker, DatePickerProps, Empty, Select, Spin } from "antd";
import { useDatesFilter } from "../answersTab/helpers";
import { RangePickerProps } from "antd/es/date-picker";
import { DownloadOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import {
  ChartQuestionAnswersInfoType,
  ChartQuestionAnswersResponseType,
} from "../../../../../../types/chartQuestionAnswersInfoType";
import { Dayjs } from "dayjs";
import { DATE_TIME_FORMAT } from "../../../../../../utils";
import { useActions } from "../../../../../../hooks/useActions";
import StatisticsLayout from "../../../../../../components/statisticsLayout";
import { chartsMapping } from "../../../../../../components/charts/chartsMapping";
import { NPSDataState } from "../../../../../../types/npsTypes";
import { SpecialChartResponseType } from "../../../../../../types/specialChartType";
import { TemplateEnum } from "../../../../../../types/templateEnum";
import { specialChartsMapping } from "../../../../../../components/charts/specialChartsMappig";
import { CSIData } from "../../../../../../types/csi";
import { CSITable } from "../../../../../../components/CSITable";
import * as reportsService from "../../../../../../services/reports/reportsService";
import { FilterStatus } from "../../../../../../types/filterStatusValue";
import { getStatusFilterOptions } from "../../../../../../utils/getStatusFilterOptions";
import { debug } from "../../../../../../utils/debug";

const ReportsTab: FC = () => {
  const { id: surveyId = "" } = useParams();
  const survey = useSelector((state: RootState) => state.surveys?.current);
  const [lang, setLang] = useState<string | undefined>();
  const [canApply, setCalApply] = useState(false);
  const [data, setData] = useState<ChartQuestionAnswersResponseType>([]);
  const [npsData, setNpsData] = useState<NPSDataState>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [specialChartData, setSpecialChartData] = useState<SpecialChartResponseType>([]);
  const [csiData, setCsiData] = useState<Record<string, CSIData>>({});
  const [filterStatus, setFilterStatus] = useState<FilterStatus>(FilterStatus.ALL);
  const { selectedDates, setSelectedDates, resetDates } = useDatesFilter();
  const { getSurveyReportPdf } = useActions();
  const statusOption = getStatusFilterOptions();

  const onDatesPick = (value: DatePickerProps["value"] | RangePickerProps["value"]) => {
    if (value === null) {
      resetDates();
    } else if (Array.isArray(value)) {
      setSelectedDates(value);
    }
    setCalApply(true);
  };

  const loadNPSData = useCallback(
    async (questionId: string, from?: string, to?: string, state?: string) => {
      try {
        const response = await reportsService.getNPSData(surveyId, questionId, from, to, state);
        setNpsData((prev) => ({
          ...prev,
          [questionId]: response,
        }));
      } catch (error) {
        debug.error("Error loading NPS data:", error);
      }
    },
    [surveyId],
  );

  const loadSpecialChartData = useCallback(
    async (questionId: string, from?: string, to?: string, state?: string) => {
      try {
        const response = await reportsService.getSpecialChartData(surveyId, questionId, from, to, state);
        setSpecialChartData((prev) => {
          const existingData = prev.filter((item) => String(item.questionId) !== String(questionId));
          return [...existingData, ...response];
        });
      } catch (error) {
        debug.error("Error loading special chart data: ", error);
      }
    },
    [surveyId],
  );

  const loadCSIData = useCallback(
    async (questionId: string, from?: string, to?: string, state?: string) => {
      try {
        const response = await reportsService.getCSIData(surveyId, questionId, from, to, state);
        setCsiData((prev) => ({
          ...prev,
          [questionId]: response,
        }));
      } catch (error) {
        debug.error("Error loading CSI data:", error);
      }
    },
    [surveyId],
  );

  const loadAllData = useCallback(
    async (from?: string, to?: string, state: string = FilterStatus.ALL) => {
      setIsLoading(true);
      try {
        const chartData = await reportsService.getChartData(surveyId, from, to, state);
        setData(chartData);

        const npsQuestions = chartData.filter(
          (item: ChartQuestionAnswersInfoType) => item.templateType === "LOYALTY_INDEX",
        );

        await Promise.all(
          npsQuestions.map((question: ChartQuestionAnswersInfoType) =>
            loadNPSData(question.questionId, from, to, state),
          ),
        );

        const ranging = chartData.filter(
          (item: ChartQuestionAnswersInfoType) =>
            item.templateType === "RANGING" || item.templateType === "LIMITED_RANGING",
        );
        await Promise.all(
          ranging.map((question: ChartQuestionAnswersInfoType) =>
            loadSpecialChartData(question.questionId, from, to, state),
          ),
        );

        const csiQuestions = chartData.filter((item: ChartQuestionAnswersInfoType) => item.templateType === "CSI");
        await Promise.all(
          csiQuestions.map((question: ChartQuestionAnswersInfoType) =>
            loadCSIData(question.questionId, from, to, state),
          ),
        );
      } catch (error) {
        debug.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [surveyId, loadNPSData, loadSpecialChartData, loadCSIData],
  );

  const getCurrentFilters = useCallback(() => {
    const from = selectedDates[0] ? (selectedDates[0] as Dayjs).format(DATE_TIME_FORMAT) : undefined;
    const to = selectedDates[1] ? (selectedDates[1] as Dayjs).format(DATE_TIME_FORMAT) : undefined;
    const state = filterStatus === FilterStatus.ALL ? undefined : filterStatus;
    return { from, to, state };
  }, [selectedDates, filterStatus]);

  const applyFilters = useCallback(() => {
    if (canApply) {
      const { from, to, state } = getCurrentFilters();
      loadAllData(from, to, state).then();
      setCalApply(false);
    }
  }, [canApply, getCurrentFilters, loadAllData]);

  const handleDownload = useCallback(() => {
    if (surveyId) {
      getSurveyReportPdf(surveyId);
    }
  }, [surveyId, getSurveyReportPdf]);

  useEffect(() => {
    setLang(survey?.languages?.at(0)?.langCode);
  }, [survey?.languages]);

  useEffect(() => {
    loadAllData().then();
  }, [loadAllData]);

  const renderChart = (item: ChartQuestionAnswersInfoType) => {
    if (item.templateType === "CSI") {
      return <CSITable key={item.questionId} data={csiData[item.questionId] || []} />;
    }

    const ChartComponent = chartsMapping[item.templateType];

    if (ChartComponent) {
      const chartProps = {
        allData: [item],
        language: lang,
        ...(item.templateType === "LOYALTY_INDEX" && {
          NPSData: Object.keys(npsData).map((key) => ({ [key]: npsData[key] })),
        }),
      };

      return <ChartComponent key={item.questionId} {...chartProps} />;
    }

    return null;
  };

  const renderSpecialCharts = (items: SpecialChartResponseType, questionType: string) => {
    const groupedItems = items.reduce((acc, item) => {
      if (!acc[item.questionId]) {
        acc[item.questionId] = [];
      }
      acc[item.questionId].push(item);
      return acc;
    }, {} as Record<string, SpecialChartResponseType>);

    return Object.entries(groupedItems).map(([questionId, groupData]) => {
      const ChartComponent = specialChartsMapping[questionType as TemplateEnum];

      if (ChartComponent) {
        return (
          <div key={questionId}>
            <ChartComponent allData={groupData} />
          </div>
        );
      }
      return null;
    });
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", marginBottom: 10 }}>
        <DatePicker.RangePicker
          showTime={{ format: "HH:mm" }}
          format="YYYY-MM-DD HH:mm"
          onChange={onDatesPick}
          value={selectedDates}
          inputReadOnly
        />
        <Select
          value={filterStatus}
          style={{ width: 160 }}
          onChange={(value: FilterStatus) => {
            setFilterStatus(value);
            setCalApply(true);
          }}
          placeholder={I18n.t("selectStatus")}
        >
          {statusOption.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        <Button onClick={applyFilters} type={"primary"} style={{ marginRight: 30 }} disabled={!canApply}>
          <Translate value={"display"} />
        </Button>
        <Select
          value={lang}
          style={{ width: 160 }}
          onChange={(value) => setLang(value)}
          placeholder={I18n.t("selectLanguage")}
        >
          {survey?.languages?.map((lang, index) => (
            <Select.Option key={index} value={lang.langCode}>
              {lang.langName}
            </Select.Option>
          ))}
        </Select>
        <Button type="default" icon={<DownloadOutlined />} onClick={handleDownload} style={{ marginLeft: "auto" }}>
          <Translate value="download" />
        </Button>
      </div>
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
          <Spin />
        </div>
      ) : (
        <>
          {data
            .sort((a, b) => a.showOrder - b.showOrder)
            .map((item) => {
              if (item.templateType === "RANGING" || item.templateType === "LIMITED_RANGING") {
                const questionSpecialData = specialChartData.filter(
                  (specialItem) => String(specialItem.questionId) === String(item.questionId),
                );

                return questionSpecialData.length > 0 ? (
                  <StatisticsLayout key={item.questionId} title={`${item.showOrder - 1}: ${item.questionName}`}>
                    {renderSpecialCharts(questionSpecialData, questionSpecialData[0]?.answerType || item.templateType)}
                  </StatisticsLayout>
                ) : null;
              } else {
                const chart = renderChart(item);
                return chart ? (
                  <StatisticsLayout
                    key={item.questionId}
                    title={`${item.showOrder - 1}: ${item.questionName}`}
                    padding="20px 20px"
                  >
                    {chart}
                  </StatisticsLayout>
                ) : null;
              }
            })}

          {data.length === 0 && (
            <Empty
              style={{
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ReportsTab;
